import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HistoryIcon from "@mui/icons-material/History";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import React from "react";
import { formatUSPhone } from "../../../../utils";
import { BaseModalComponent } from "../../../common/BaseModalComponent";
import { ClientView } from "../../../invoice/view/components/ClientView";
import { IBillingPeriod, IBillingSubscriptionItem, IPaymentAttemptItem } from "../interfaces/billingSubscriptions.interface";

interface SubscriptionPaymentHistoryModalProps {
  subscription: IBillingSubscriptionItem;
}

const COLORS = {
  primary: "#FF6B00",
  primaryLight: "#FFF3E8",

  text: "#1F2937",
  secondary: "#64748B",
  muted: "#94A3B8",

  border: "#E5E7EB",
  background: "#F9FAFB",
  white: "#FFFFFF",

  success: "#15803D",
  successBg: "#DCFCE7",

  warning: "#B45309",
  warningBg: "#FEF3C7",

  danger: "#B91C1C",
  dangerBg: "#FEE2E2",
};

export const SubscriptionPaymentHistoryModal: React.FC<SubscriptionPaymentHistoryModalProps> = ({ subscription }) => {
  const billingPeriods = subscription?.billingPeriods || [];

  const parsePhoneNumber = (sub: any): string => {
    let metaObj = sub?.meta;

    if (typeof metaObj === "string") {
      try {
        metaObj = JSON.parse(metaObj);
      } catch {
        metaObj = {};
      }
    }

    if (typeof metaObj === "string") {
      try {
        metaObj = JSON.parse(metaObj);
      } catch {
        metaObj = {};
      }
    }

    const rawPhone = metaObj?.phoneNumber;

    if (!rawPhone) return "Unassigned Phone Number";

    return formatUSPhone(rawPhone) || rawPhone;
  };

  const phoneNumber = parsePhoneNumber(subscription);
  const planName = subscription?.plan?.product?.name || "Standard Plan";

  const getStatusConfig = (status?: string) => {
    const norm = (status || "").toUpperCase();

    if (["PAID", "COMPLETED", "ACTIVE"].includes(norm)) {
      return {
        label: norm === "ACTIVE" ? "ACTIVE" : "PAID",
        color: COLORS.success,
        bg: COLORS.successBg,
        border: "#BBF7D0",
        icon: <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />,
      };
    }

    if (["PAYMENT_FAILED", "FAILED", "PAST_DUE"].includes(norm)) {
      return {
        label: norm === "PAST_DUE" ? "PAST DUE" : "PAYMENT FAILED",
        color: COLORS.danger,
        bg: COLORS.dangerBg,
        border: "#FECACA",
        icon: <ErrorOutlineIcon sx={{ fontSize: 16 }} />,
      };
    }

    if (["PENDING", "CALCULATED", "OPEN", "PROCESSING"].includes(norm)) {
      return {
        label: norm,
        color: COLORS.warning,
        bg: COLORS.warningBg,
        border: "#FDE68A",
        icon: <HistoryIcon sx={{ fontSize: 16 }} />,
      };
    }

    if (["CANCELED", "CANCELLED"].includes(norm)) {
      return {
        label: "CANCELED",
        color: COLORS.secondary,
        bg: "#F1F5F9",
        border: COLORS.border,
        icon: <HistoryIcon sx={{ fontSize: 16 }} />,
      };
    }

    return {
      label: norm || "UNKNOWN",
      color: COLORS.secondary,
      bg: "#F1F5F9",
      border: COLORS.border,
      icon: <HistoryIcon sx={{ fontSize: 16 }} />,
    };
  };

  const formatDate = (dateStr?: string | Date | null, formatStr = "MMM DD, YYYY HH:mm") => {
    if (!dateStr || !dayjs(dateStr).isValid()) return "N/A";

    return dayjs(dateStr).format(formatStr);
  };

  const formatAmount = (value: number | string | null | undefined) => {
    const amount = Number(value ?? 0);

    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number.isFinite(amount) ? amount : 0);
  };

  return (
    <Box
      sx={{
        p: { xs: 1.5, sm: 3 },
        maxWidth: "100%",
        bgcolor: COLORS.background,
        minHeight: "100%",
      }}
    >
      {/* Subscription header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 2.5 },
          mb: 3,
          borderRadius: "16px",
          bgcolor: COLORS.white,
          border: `1px solid ${COLORS.border}`,
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={8}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 46,
                  height: 46,
                  borderRadius: "12px",
                  bgcolor: COLORS.background,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <HistoryIcon sx={{ fontSize: 25, color: COLORS.secondary }} />
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    color: COLORS.text,
                    lineHeight: 1.3,
                  }}
                >
                  Payment History
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: COLORS.secondary,
                    fontWeight: 500,
                    mt: 0.35,
                    overflowWrap: "anywhere",
                  }}
                >
                  {planName} · {phoneNumber}
                </Typography>
              </Box>
            </Stack>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Box
              sx={{
                textAlign: { xs: "left", sm: "right" },
                p: 1.5,
                borderRadius: "12px",
                bgcolor: COLORS.background,
                border: `1px solid ${COLORS.border}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  color: COLORS.muted,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  mb: 0.5,
                }}
              >
                Subscription ID
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: COLORS.secondary,
                  fontFamily: "monospace",
                  overflowWrap: "anywhere",
                }}
              >
                {subscription.paypalId || subscription.id}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Billing periods heading */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              color: COLORS.text,
              letterSpacing: "-0.025em",
            }}
          >
            Billing Periods
          </Typography>

          <Typography variant="body2" sx={{ color: COLORS.secondary, mt: 0.25 }}>
            Review billing cycles, charges and payment details.
          </Typography>
        </Box>

        <Chip
          label={`${billingPeriods.length} ${billingPeriods.length === 1 ? "period" : "periods"}`}
          size="small"
          sx={{
            bgcolor: COLORS.white,
            color: COLORS.secondary,
            border: `1px solid ${COLORS.border}`,
            fontWeight: 700,
            borderRadius: "9px",
            height: 30,
            "& .MuiChip-label": { px: 1.5 },
          }}
        />
      </Stack>

      {/* Empty state */}
      {billingPeriods.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 5 },
            textAlign: "center",
            borderRadius: "16px",
            bgcolor: COLORS.white,
            border: `1px solid ${COLORS.border}`,
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              mx: "auto",
              mb: 2,
              borderRadius: "16px",
              bgcolor: COLORS.background,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ReceiptLongIcon sx={{ fontSize: 32, color: COLORS.muted }} />
          </Box>

          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: COLORS.text }}>
            No billing periods yet
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: COLORS.secondary,
              mt: 0.75,
              maxWidth: 360,
              mx: "auto",
              lineHeight: 1.7,
            }}
          >
            Billing cycle records will appear here when they become available for this subscription.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {billingPeriods.map((period: IBillingPeriod, idx: number) => {
            const attempts: IPaymentAttemptItem[] = period.paymentAttempts || [];

            const currency = period.currencyCode || "USD";
            const periodNorm = (period.status || "").toUpperCase();

            const isFailed = ["PAYMENT_FAILED", "FAILED", "PAST_DUE"].includes(periodNorm);

            const isPaidStatus = ["PAID", "COMPLETED", "ACTIVE"].includes(periodNorm);

            const isCanceled = ["CANCELED", "CANCELLED"].includes(periodNorm);

            const isUnpaid = !isPaidStatus && !isCanceled;
            const statusConfig = getStatusConfig(period.status);

            const invoicePaidAt = (period as any)?.invoice?.paidAt as string | null | undefined;

            const chargedAt = period.paidAt || invoicePaidAt;

            const billingDateLabel = chargedAt
              ? formatDate(chargedAt, "MMM DD, YYYY · HH:mm")
              : isPaidStatus
                ? "Date not recorded"
                : "Awaiting payment";

            const invoiceUid = period.invoiceId || (period as any)?.invoiceUid || (period as any)?.invoice?.uid;

            const startDate = formatDate(period.periodStart, "MMM DD, YYYY");

            const endDate = formatDate(period.periodEnd, "MMM DD, YYYY");

            return (
              <Card
                key={period.id || idx}
                elevation={0}
                sx={{
                  overflow: "hidden",
                  borderRadius: "16px",
                  bgcolor: COLORS.white,
                  border: `1px solid ${COLORS.border}`,
                  boxShadow: "none",
                  transition: "box-shadow 180ms ease",
                  "&:hover": {
                    boxShadow: "0 4px 16px rgba(15, 23, 42, 0.04)",
                  },
                }}
              >
                <CardContent
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    "&:last-child": { pb: { xs: 2, sm: 2.5 } },
                  }}
                >
                  {/* Period top row */}
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    spacing={1.5}
                    sx={{ mb: 2.5 }}
                  >
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <Box
                        sx={{
                          width: 42,
                          height: 42,
                          borderRadius: "12px",
                          bgcolor: COLORS.background,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <CalendarMonthRoundedIcon
                          sx={{
                            color: COLORS.secondary,
                            fontSize: 23,
                          }}
                        />
                      </Box>

                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 800,
                            color: COLORS.text,
                            lineHeight: 1.35,
                          }}
                        >
                          Billing cycle #{billingPeriods.length - idx}
                        </Typography>

                        <Typography
                          variant="caption"
                          sx={{
                            color: COLORS.secondary,
                            display: "block",
                            mt: 0.35,
                            overflowWrap: "anywhere",
                          }}
                        >
                          {period.id ? `Reference: ${period.id}` : "Billing period details"}
                        </Typography>
                      </Box>
                    </Stack>

                    <Chip
                      icon={statusConfig.icon}
                      label={statusConfig.label}
                      size="small"
                      sx={{
                        bgcolor: statusConfig.bg,
                        color: statusConfig.color,
                        border: `1px solid ${statusConfig.border}`,
                        fontWeight: 800,
                        fontSize: "0.7rem",
                        height: 29,
                        borderRadius: "9px",
                        "& .MuiChip-icon": {
                          color: statusConfig.color,
                          ml: 1,
                        },
                        "& .MuiChip-label": {
                          px: 1,
                          pr: 1.25,
                        },
                      }}
                    />
                  </Stack>

                  {/* Billing period date range */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 1.5, sm: 2 },
                      mb: 2,
                      borderRadius: "12px",
                      bgcolor: COLORS.background,
                      border: `1px solid ${COLORS.border}`,
                    }}
                  >
                    <Typography
                      variant="overline"
                      sx={{
                        display: "block",
                        color: COLORS.secondary,
                        fontSize: "0.67rem",
                        fontWeight: 800,
                        letterSpacing: "0.09em",
                        lineHeight: 1.5,
                        mb: 1.5,
                      }}
                    >
                      BILLING PERIOD
                    </Typography>

                    <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "stretch", sm: "center" }} spacing={{ xs: 1.5, sm: 2 }}>
                      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: "10px",
                            bgcolor: COLORS.white,
                            border: `1px solid ${COLORS.border}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <EventAvailableRoundedIcon
                            sx={{
                              color: COLORS.secondary,
                              fontSize: 19,
                            }}
                          />
                        </Box>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              display: "block",
                              color: COLORS.muted,
                              fontWeight: 700,
                              mb: 0.25,
                            }}
                          >
                            Period starts
                          </Typography>

                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 800,
                              color: COLORS.text,
                              overflowWrap: "anywhere",
                            }}
                          >
                            {startDate}
                          </Typography>

                          <Typography variant="caption" sx={{ color: COLORS.secondary }}>
                            {formatDate(period.periodStart, "HH:mm")}
                          </Typography>
                        </Box>
                      </Stack>

                      <Box
                        sx={{
                          display: { xs: "none", sm: "flex" },
                          alignItems: "center",
                          justifyContent: "center",
                          color: COLORS.muted,
                        }}
                      >
                        <ArrowForwardRoundedIcon />
                      </Box>

                      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: "10px",
                            bgcolor: COLORS.white,
                            border: `1px solid ${COLORS.border}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <CalendarMonthRoundedIcon
                            sx={{
                              color: COLORS.secondary,
                              fontSize: 19,
                            }}
                          />
                        </Box>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              display: "block",
                              color: COLORS.muted,
                              fontWeight: 700,
                              mb: 0.25,
                            }}
                          >
                            Period ends
                          </Typography>

                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 800,
                              color: COLORS.text,
                              overflowWrap: "anywhere",
                            }}
                          >
                            {endDate}
                          </Typography>

                          <Typography variant="caption" sx={{ color: COLORS.secondary }}>
                            {formatDate(period.periodEnd, "HH:mm")}
                          </Typography>
                        </Box>
                      </Stack>
                    </Stack>
                  </Paper>

                  {/* Amount, payment date and invoice */}
                  <Grid container spacing={2} alignItems="stretch">
                    {/* Total amount */}
                    <Grid item xs={12} sm={6} md={4}>
                      <Paper
                        elevation={0}
                        sx={{
                          height: "100%",
                          p: 2,
                          borderRadius: "12px",
                          border: `1px solid ${COLORS.border}`,
                          bgcolor: COLORS.white,
                        }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                          <PaymentsRoundedIcon
                            sx={{
                              color: COLORS.secondary,
                              fontSize: 19,
                            }}
                          />

                          <Typography
                            variant="caption"
                            sx={{
                              color: COLORS.secondary,
                              fontWeight: 700,
                            }}
                          >
                            Total amount
                          </Typography>
                        </Stack>

                        <Typography
                          sx={{
                            fontSize: { xs: "1.5rem", sm: "1.7rem" },
                            lineHeight: 1.2,
                            letterSpacing: "-0.04em",
                            fontWeight: 900,
                            color: COLORS.text,
                            overflowWrap: "anywhere",
                          }}
                        >
                          {currency} ${formatAmount(period.totalAmount)}
                        </Typography>

                        {Number(period.usageAmount) > 0 && (
                          <Box sx={{ mt: 1.5 }}>
                            <Divider
                              sx={{
                                mb: 1.25,
                                borderColor: COLORS.border,
                              }}
                            />

                            <Stack spacing={0.75}>
                              <Stack direction="row" justifyContent="space-between" spacing={1}>
                                <Typography variant="caption" sx={{ color: COLORS.secondary }}>
                                  Base amount
                                </Typography>

                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 700,
                                    color: COLORS.text,
                                  }}
                                >
                                  ${formatAmount(period.baseAmount)}
                                </Typography>
                              </Stack>

                              <Stack direction="row" justifyContent="space-between" spacing={1}>
                                <Typography variant="caption" sx={{ color: COLORS.secondary }}>
                                  Usage amount
                                </Typography>

                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 700,
                                    color: COLORS.text,
                                  }}
                                >
                                  ${formatAmount(period.usageAmount)}
                                </Typography>
                              </Stack>
                            </Stack>
                          </Box>
                        )}
                      </Paper>
                    </Grid>

                    {/* Payment date */}
                    <Grid item xs={12} sm={6} md={4}>
                      <Paper
                        elevation={0}
                        sx={{
                          height: "100%",
                          p: 2,
                          borderRadius: "12px",
                          border: `1px solid ${COLORS.border}`,
                          bgcolor: COLORS.white,
                        }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
                          <HistoryIcon
                            sx={{
                              color: COLORS.secondary,
                              fontSize: 19,
                            }}
                          />

                          <Typography
                            variant="caption"
                            sx={{
                              color: COLORS.secondary,
                              fontWeight: 700,
                            }}
                          >
                            Payment date
                          </Typography>
                        </Stack>

                        <Typography
                          variant="body2"
                          sx={{
                            color: chargedAt ? COLORS.text : isFailed ? COLORS.danger : COLORS.secondary,
                            fontWeight: 800,
                            lineHeight: 1.5,
                          }}
                        >
                          {billingDateLabel}
                        </Typography>

                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            color: COLORS.muted,
                            mt: 0.75,
                          }}
                        >
                          {chargedAt ? "Payment timestamp" : isFailed ? "Payment was not completed" : "No payment confirmation yet"}
                        </Typography>
                      </Paper>
                    </Grid>

                    {/* Invoice details */}
                    <Grid item xs={12} md={4}>
                      <Box
                        sx={{
                          height: "100%",
                          minHeight: 110,
                          p: 2,
                          borderRadius: "12px",
                          bgcolor: COLORS.white,
                          border: `1px solid ${COLORS.border}`,
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: { xs: "stretch", md: "flex-start" },
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            color: COLORS.secondary,
                            fontWeight: 700,
                            mb: 1.25,
                          }}
                        >
                          Invoice details
                        </Typography>

                        {invoiceUid ? (
                          <BaseModalComponent
                            modalKey={`billing-period-invoice-${invoiceUid}-${period.id || idx}`}
                            modalLabel={`Invoice · ${periodNorm === "PAID" ? "Details" : "Pay / View"}`}
                            defaultSizes={{ width: 1100, height: 800 }}
                            elementTrigger={
                              <Button
                                variant="contained"
                                fullWidth
                                startIcon={<ReceiptLongIcon />}
                                sx={{
                                  textTransform: "none",
                                  fontWeight: 800,
                                  fontSize: "0.8rem",
                                  borderRadius: "10px",
                                  py: 1.15,
                                  px: 1.5,
                                  bgcolor: isUnpaid ? COLORS.primary : COLORS.background,
                                  color: isUnpaid ? COLORS.white : COLORS.text,
                                  border: isUnpaid ? "1px solid transparent" : `1px solid ${COLORS.border}`,
                                  boxShadow: "none",
                                  "&:hover": {
                                    bgcolor: isUnpaid ? "#E85D00" : "#F3F4F6",
                                    boxShadow: "none",
                                  },
                                }}
                              >
                                {isUnpaid ? "Pay / View Invoice" : "View Invoice"}
                              </Button>
                            }
                          >
                            <Box
                              sx={{
                                width: "100%",
                                height: "100%",
                                bgcolor: COLORS.white,
                                overflowY: "auto",
                              }}
                            >
                              <ClientView invoiceUid={invoiceUid} />
                            </Box>
                          </BaseModalComponent>
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{
                              color: COLORS.secondary,
                              fontWeight: 600,
                            }}
                          >
                            No invoice available
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Payment attempts */}
                  {attempts.length > 0 && (
                    <Box sx={{ mt: 2.5 }}>
                      <Divider
                        sx={{
                          mb: 2,
                          borderColor: COLORS.border,
                        }}
                      />

                      <Accordion
                        elevation={0}
                        disableGutters
                        sx={{
                          bgcolor: COLORS.white,
                          borderRadius: "12px",
                          border: `1px solid ${COLORS.border}`,
                          overflow: "hidden",
                          "&:before": { display: "none" },
                          "&.Mui-expanded": { mb: 0 },
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon sx={{ color: COLORS.secondary }} />}
                          sx={{
                            minHeight: 54,
                            px: 2,
                            "& .MuiAccordionSummary-content": {
                              my: 1.5,
                            },
                            "& .MuiAccordionSummary-content.Mui-expanded": {
                              my: 1.5,
                            },
                          }}
                        >
                          <Stack direction="row" spacing={1.25} alignItems="center">
                            <Box
                              sx={{
                                width: 34,
                                height: 34,
                                borderRadius: "10px",
                                bgcolor: COLORS.background,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <HistoryIcon
                                sx={{
                                  color: COLORS.secondary,
                                  fontSize: 18,
                                }}
                              />
                            </Box>

                            <Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: COLORS.text,
                                  fontWeight: 800,
                                }}
                              >
                                Payment execution log
                              </Typography>

                              <Typography variant="caption" sx={{ color: COLORS.secondary }}>
                                {attempts.length} {attempts.length === 1 ? "attempt" : "attempts"} recorded
                              </Typography>
                            </Box>
                          </Stack>
                        </AccordionSummary>

                        <AccordionDetails
                          sx={{
                            p: { xs: 1.5, sm: 2 },
                            bgcolor: COLORS.background,
                            borderTop: `1px solid ${COLORS.border}`,
                          }}
                        >
                          <Stack spacing={1.25}>
                            {attempts.map((attempt, attIdx) => (
                              <Paper
                                key={attIdx}
                                elevation={0}
                                sx={{
                                  p: 1.75,
                                  borderRadius: "10px",
                                  bgcolor: COLORS.white,
                                  border: `1px solid ${COLORS.border}`,
                                }}
                              >
                                <Stack
                                  direction={{
                                    xs: "column",
                                    sm: "row",
                                  }}
                                  justifyContent="space-between"
                                  alignItems={{
                                    xs: "flex-start",
                                    sm: "center",
                                  }}
                                  spacing={1}
                                >
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    {attempt.success ? (
                                      <CheckCircleOutlineIcon
                                        sx={{
                                          color: COLORS.success,
                                          fontSize: 20,
                                        }}
                                      />
                                    ) : (
                                      <ErrorOutlineIcon
                                        sx={{
                                          color: COLORS.danger,
                                          fontSize: 20,
                                        }}
                                      />
                                    )}

                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 800,
                                        color: COLORS.text,
                                      }}
                                    >
                                      {attempt.success ? "Execution successful" : "Execution failed"}
                                    </Typography>
                                  </Stack>

                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: COLORS.secondary,
                                      fontWeight: 600,
                                    }}
                                  >
                                    {formatDate(attempt.timestamp, "MMM DD, YYYY · HH:mm:ss")}
                                  </Typography>
                                </Stack>

                                {attempt.errorMessage && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: COLORS.danger,
                                      mt: 1,
                                      display: "block",
                                      fontWeight: 600,
                                      overflowWrap: "anywhere",
                                    }}
                                  >
                                    Error: {attempt.errorMessage}
                                  </Typography>
                                )}

                                {attempt.rawResponse?.id && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: COLORS.secondary,
                                      mt: 1,
                                      display: "block",
                                      fontFamily: "monospace",
                                      overflowWrap: "anywhere",
                                    }}
                                  >
                                    PayPal Status: {attempt.rawResponse.status || "N/A"} · Plan ID: {attempt.rawResponse.plan_id || "N/A"}
                                  </Typography>
                                )}
                              </Paper>
                            ))}
                          </Stack>
                        </AccordionDetails>
                      </Accordion>
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

export default SubscriptionPaymentHistoryModal;
